/**
 * @fileoverview Flexible button component with multiple style variants
 * @active Used throughout the application for buttons and interactive elements
 * 
 * Provides a customizable button implementation with:
 * - Multiple visual style variants (blue, red, white, outline, text, etc.)
 * - Disabled state handling
 * - Custom styling support
 * - Click handler integration
 * 
 * This component serves as the primary button implementation across the app
 * and is used for tags, navigation controls, and action buttons.
 * 
 * @see {@link src/components/blog/types.ts} - CustomElementProps interface
 * @see {@link src/components/blog/categoriesList/CategoriesList.tsx} - Usage in blog categories
 * @see {@link src/components/research/components/titleBlock/TitleBlock.tsx} - Usage in research page
 * @see {@link src/styles/fontStyles.ts} - Font style utilities
 */
"use client";

import { Flex } from "@chakra-ui/react";

import type { CustomElementProps } from "@/components/blog/types";
import {
  h_400_14_100_48_trim,
  h_700_14_100,
  h_700_14_120_28,
  h_400_10_135_04,
} from "@/styles/fontStyles";

export const CustomElement = ({
  title, // Title text to display
  variant, // Button style variant
  isDisabled = false, // Whether the button is disabled
  // isLoading = false, // Whether the button is in loading state
  children, // Child elements
  handler, // Click handler function
  customStyle, // Additional custom styles
}: CustomElementProps) => {
  /**
   * Base style for all button variants
   */
  const baseStyle = {
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  };

  /**
   * Common styles for colored and outline buttons
   */
  const colorAndOutLineBtnStyle = {
    ...baseStyle,
    ...h_700_14_100,
    p: "15px 24px 12px 24px",
  };

  /**
   * Styles for primary colored buttons with disabled state handling
   */
  const colorBtnStyle = {
    ...colorAndOutLineBtnStyle,
    color: !isDisabled ? "white" : "disabledColor",
    bgColor: !isDisabled ? "mainBlue" : "disabledBg",
    pointerEvents: isDisabled ? "none" : "auto" as "none" | "auto",
    p: "18px 24px 14px 24px",
    _hover: {
      bgColor: !isDisabled ? "blue" : "disabledBg",
    },
    _disabled: {
      bgColor: "disabledBg",
      color: "disabledColor",
    },
  };

  /**
   * Styles for small colored buttons
   */
  const colorSmallBtnStyle = {
    ...h_400_10_135_04,
    color: "white",
    p: "8px 8px 4px 8px",
    borderRadius: "4px",
  };

  /**
   * Styles for outline buttons with hover effects
   */
  const outlineBtnStyle = {
    ...baseStyle,
    ...colorAndOutLineBtnStyle,
    color: "gray",
    bgColor: "transparent",
    border: "1px solid",
    borderColor: "gray",
    _hover: {
      color: "mainBlue",
      bgColor: "transparent",
      borderColor: "mainBlue",
    },
  };

  /**
   * Styles for text-only buttons
   */
  const textBtnStyle = {
    ...baseStyle,
    ...h_700_14_100,
    w: "86px",
    h: "46px",
    color: "gray",
    bgColor: "transparent",
    _hover: { color: "mainBlue", bgColor: "transparent" },
  };

  /**
   * Styles for white background buttons
   */
  const whiteBtnStyle = {
    ...baseStyle,
    w: "fit-content",
    h: "32px",
    bgColor: "white",
    color: "naturalBlack",
    p: "14px 20px 12px 20px",
    ...h_400_14_100_48_trim,
    _hover: {
      bgColor: "white",
      boxShadow: "0px 0px 14px 0px rgba(114, 114, 114, 0.25)",
    },
  };

  /**
   * Final style based on selected variant
   */
  let variantStyle;

  // Apply variant-specific styles
  switch (variant) {
    case "blueBtn":
      variantStyle = { ...colorBtnStyle, ...h_700_14_120_28 };
      break;
    case "redBtn":
      variantStyle = {
        ...colorBtnStyle,
        bgColor: "red",
        _hover: { bgColor: "darkRedNew" },
      };
      break;
    case "smallRedBtn":
      variantStyle = { ...colorSmallBtnStyle, bgColor: "red" };
      break;
    case "smallBlueBtn":
      variantStyle = { ...colorSmallBtnStyle, bgColor: "mainBlue" };
      break;
    case "smallGreenBtn":
      variantStyle = { ...colorSmallBtnStyle, bgColor: "green" };
      break;
    case "smallGrayBtn":
      variantStyle = { ...colorSmallBtnStyle, bgColor: "gray" };
      break;
    case "smallWhiteBtn":
      variantStyle = { ...colorSmallBtnStyle, bgColor: "white" };
      break;
    case "outlineBtn":
      variantStyle = { ...outlineBtnStyle };
      break;
    case "backBtn":
      variantStyle = {
        ...textBtnStyle,
        color: "blackColor",
        _hover: { color: "blue" },
      };
      break;
    case "seeMoreBtn":
      variantStyle = { ...textBtnStyle };
      break;
    case "whiteBtn":
      variantStyle = { ...whiteBtnStyle };
      break;
    case "whiteBtnOutline":
      variantStyle = {
        ...whiteBtnStyle,
        border: "1.5px solid",
        borderColor: "menuGray",
      };
      break;
  }
  return (
    <Flex
      {...variantStyle}
      transition={"all 0.25s ease-in-out"}
      onClick={handler}
      whiteSpace={"nowrap"}
      {...customStyle}
    >
      {title && title}
      {children && children}
    </Flex>
  );
};
